import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ProductCard } from "@/components/store/ProductCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Flame } from "lucide-react";
import { CountdownTimer } from "@/components/store/CountdownTimer";
import { Badge } from "@/components/ui/badge";
import { HeroBannerSlider } from "@/components/store/HeroBannerSlider";
import { CategoryStrip } from "@/components/store/CategoryStrip";

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  stock: number;
  category: string | null;
  category_id: string | null;
  featured: boolean;
  on_sale: boolean | null;
  sale_price: number | null;
}

interface Category {
  id: string;
  name: string;
  slug: string | null;
}

const PREVIEW_COUNT = 8;

const Home = () => {
  const [params] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const search = params.get("q")?.toLowerCase() ?? "";
  const catParam = params.get("cat");

  useEffect(() => {
    if (catParam) setShowAll(true);
  }, [catParam]);

  useEffect(() => {
    if (search) setShowAll(true);
  }, [search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [{ data: prods }, { data: cats }] = await Promise.all([
        supabase.from("products").select("*").order("created_at", { ascending: false }),
        supabase.from("categories").select("id, name, slug"),
      ]);
      setProducts((prods as Product[]) ?? []);
      setCategories((cats as Category[]) ?? []);
      setLoading(false);
    };
    load();
  }, []);

  // Resolve the category from URL param (slug, id, or name) into a single Category
  const activeCategory = useMemo(() => {
    if (!catParam) return null;
    const key = catParam.toLowerCase().trim();
    return (
      categories.find((c) => (c.slug ?? "").toLowerCase() === key) ||
      categories.find((c) => c.id.toLowerCase() === key) ||
      categories.find((c) => c.name.toLowerCase() === key) ||
      null
    );
  }, [catParam, categories]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      let matchCat = true;
      if (catParam) {
        if (activeCategory) {
          matchCat =
            p.category_id === activeCategory.id ||
            (p.category ?? "").toLowerCase() === activeCategory.name.toLowerCase();
        } else {
          // fallback: legacy text match
          matchCat = (p.category ?? "").toLowerCase() === catParam.toLowerCase();
        }
      }
      const matchSearch = !search || p.name.toLowerCase().includes(search) || p.category?.toLowerCase().includes(search);
      return matchCat && matchSearch;
    });
  }, [products, catParam, activeCategory, search]);

  const visible = showAll ? filtered : filtered.slice(0, PREVIEW_COUNT);
  const hasMore = filtered.length > PREVIEW_COUNT;

  const featured = useMemo(() => products.filter((p) => p.featured).slice(0, 4), [products]);
  const onSale = useMemo(() => products.filter((p) => p.on_sale).slice(0, 4), [products]);

  return (
    <>
      {/* HERO BANNER SLIDER */}
      <HeroBannerSlider />
      <CategoryStrip />

      {/* ALL PRODUCTS / CATALOG */}
      <section
        id="shop"
        className="relative overflow-hidden bg-background pt-2 pb-8 md:pt-3 md:pb-10"
      >
        <div className="container relative">
          <div className="mb-3 md:mb-4">
            <h2 className="font-display text-lg font-bold text-center md:text-2xl">
              {search
                ? `Results for "${search}"`
                : activeCategory
                ? activeCategory.name
                : catParam
                ? catParam
                : "New Arrival Products"}
            </h2>
          </div>

          {loading ? (
            <div className="grid gap-3 sm:gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: PREVIEW_COUNT }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-square rounded-2xl bg-gradient-to-r from-secondary via-muted to-secondary bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
                  <Skeleton className="h-4 w-3/4 rounded" />
                  <Skeleton className="h-4 w-1/3 rounded" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-muted-foreground mb-4">No products found.</p>
              <Button variant="outline" asChild>
                <Link to="/">Reset filters</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:gap-5 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 animate-fade-up">
                {visible.map((p, i) => (
                  <div
                    key={p.id}
                    className="animate-fade-up"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <ProductCard product={p} />
                  </div>
                ))}
              </div>

              {/* Show All / Show Less button */}
              {hasMore && (
                <div className="mt-12 flex justify-center animate-fade-up">
                  <button
                    onClick={() => {
                      setShowAll((v) => !v);
                      if (showAll) {
                        document.getElementById("shop")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                    className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full border border-foreground/15 bg-background px-7 py-3 text-sm font-semibold text-foreground shadow-soft transition-all duration-300 hover:border-primary hover:text-primary-foreground hover:shadow-hover hover:-translate-y-0.5"
                  >
                    <span className="absolute inset-0 -z-0 origin-left scale-x-0 bg-gradient-primary transition-transform duration-500 group-hover:scale-x-100" />
                    <span className="relative z-10">
                      {showAll ? "Show less" : `View all ${filtered.length} products`}
                    </span>
                    <ArrowRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* LIMITED-TIME OFFER */}
      {onSale.length > 0 && !search && (
        <section id="sale" className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 py-16">
          <div className="container">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-10">
              <div>
                <Badge className="mb-3 gap-1.5 bg-destructive/10 text-destructive hover:bg-destructive/15 border-0">
                  <Flame className="h-3.5 w-3.5" /> Limited time
                </Badge>
                <h2 className="font-display text-3xl md:text-4xl font-bold">Limited-time offers</h2>
                <p className="text-muted-foreground mt-2">Grab these deals before the timer runs out.</p>
              </div>
              <CountdownTimer hoursFromNow={24} />
            </div>
            <div className="grid gap-6 grid-cols-2 lg:grid-cols-4">
              {onSale.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* PICKS OF THE SEASON */}
      {featured.length > 0 && !search && (
        <section className="container py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Featured</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold">Picks of the season</h2>
              <p className="text-muted-foreground mt-2">Trending favourites our customers love right now.</p>
            </div>
          </div>
          <div className="grid gap-6 grid-cols-2 lg:grid-cols-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </>
  );
};

export default Home;
